import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const BRANCH_SEARCH_RESOURCE = "branch" as const;
export const BRANCH_SEARCH_OPERATION = "search" as const;
export const BRANCH_SEARCH_API_PATH = `/v1/${BRANCH_SEARCH_RESOURCE}/${BRANCH_SEARCH_OPERATION}` as const;

export type BranchSearchParameters = paths[typeof BRANCH_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type BranchSearchBody =
  paths[typeof BRANCH_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type BranchSearchResponse =
  paths[typeof BRANCH_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useBranchSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<BranchSearchParameters, BranchSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<BranchSearchResponse, Error, BranchSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<BranchSearchParameters, BranchSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      BRANCH_SEARCH_RESOURCE,
      BRANCH_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<BranchSearchResponse>(
        BRANCH_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
